import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchInactiveProducts,
  fetchProductReportOptions,
  fetchProductSalesReport,
  type InactiveProductsReport,
  type ProductReportGroupBy,
  type ProductReportOptions,
  type ProductSalesReport,
  type ProductSalesRow,
} from "@/lib/adminProductReportsApi";
import { getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, CalendarClock, ChevronRight, Download, Loader2, Package, PackageX, TrendingUp } from "lucide-react";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";

const ALL = "__all__";

function fmt(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtNumber(value: number) {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function fmtBRDate(iso: string | null | undefined) {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

const GROUP_BY_LABELS: Record<ProductReportGroupBy, string> = {
  produto: "Produtos",
  dia: "Dia",
  cliente: "Cliente",
  unidade: "Unidade de entrega",
};

function productLabel(code: number | null, name: string) {
  return code != null ? `#${code} · ${name}` : name;
}

function downloadCsv(rows: (string | number)[][], filename: string) {
  const csv = rows.map(r => r.join(";")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type SortColumn = "code" | "name" | "unit" | "orders" | "quantity" | "value";
type SortDir = "asc" | "desc";

// Direção do 1º clique por coluna: texto/código sobem (A→Z / menor→maior); métricas descem (maior→menor).
const FIRST_DIR: Record<SortColumn, SortDir> = {
  code: "asc", name: "asc", unit: "asc", orders: "desc", quantity: "desc", value: "desc",
};

// Estado de expansão e de ordenação são locais por tabela: cada grupo (dia/cliente/unidade) tem sua
// própria instância, então expandir/ordenar num grupo não afeta outro. Ordenamos apenas o array de
// produtos — as sub-linhas de corte continuam renderizadas logo abaixo do seu produto (vínculo mantido).
function ProductsTable({ rows }: { rows: ProductSalesRow[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<SortColumn>("code");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggle = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const onSort = (col: SortColumn) => {
    if (col === sortColumn) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDir(FIRST_DIR[col]);
    }
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    const sign = sortDir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      // Código nulo (produto fora do catálogo) sempre por último, independente da direção.
      if (sortColumn === "code") {
        const an = a.productCode == null;
        const bn = b.productCode == null;
        if (an && bn) return a.productName.localeCompare(b.productName, "pt-BR");
        if (an) return 1;
        if (bn) return -1;
        return sign * ((a.productCode as number) - (b.productCode as number));
      }
      let c = 0;
      switch (sortColumn) {
        case "name": c = a.productName.localeCompare(b.productName, "pt-BR"); break;
        case "unit": c = a.unit.localeCompare(b.unit, "pt-BR"); break;
        case "orders": c = a.orderCount - b.orderCount; break;
        case "quantity": c = a.totalQuantity - b.totalQuantity; break;
        case "value": c = a.totalValue - b.totalValue; break;
      }
      if (c === 0) return a.productName.localeCompare(b.productName, "pt-BR");
      return sign * c;
    });
    return copy;
  }, [rows, sortColumn, sortDir]);

  const sortHead = (col: SortColumn, label: string, alignRight = false) => (
    <TableHead className={alignRight ? "text-right" : undefined}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 select-none hover:text-foreground"
      >
        {label}
        {sortColumn === col && (
          sortDir === "asc"
            ? <ArrowUp className="h-3 w-3" />
            : <ArrowDown className="h-3 w-3" />
        )}
      </button>
    </TableHead>
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {sortHead("code", "Cód.")}
          {sortHead("name", "Produto")}
          {sortHead("unit", "Unid.")}
          {sortHead("orders", "Pedidos", true)}
          {sortHead("quantity", "Qtd.", true)}
          {sortHead("value", "Valor", true)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map(p => {
          const key = `${p.productId}-${p.unit}`;
          const hasCuts = p.cuts.length > 0;
          const isOpen = expanded.has(key);
          return (
            <Fragment key={key}>
              <TableRow
                className={hasCuts ? "cursor-pointer" : undefined}
                onClick={hasCuts ? () => toggle(key) : undefined}
              >
                <TableCell className="tabular-nums text-muted-foreground">{p.productCode ?? "—"}</TableCell>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {hasCuts && (
                      <ChevronRight
                        className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-90")}
                      />
                    )}
                    {p.productName}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">{p.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{p.orderCount}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNumber(p.totalQuantity)} {p.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(p.totalValue)}</TableCell>
              </TableRow>
              {hasCuts && isOpen && p.cuts.map((c, idx) => (
                <TableRow key={`${key}-cut-${c.cut ?? `none-${idx}`}`} className="bg-muted/20 hover:bg-muted/30">
                  <TableCell />
                  <TableCell className="py-2 pl-8 text-sm text-muted-foreground">
                    {c.cut ?? "Sem corte"}
                  </TableCell>
                  <TableCell className="py-2 text-sm text-muted-foreground">{p.unit}</TableCell>
                  <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">{c.orderCount}</TableCell>
                  <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">{fmtNumber(c.totalQuantity)} {p.unit}</TableCell>
                  <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">{fmt(c.totalValue)}</TableCell>
                </TableRow>
              ))}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default function AdminProductReport() {
  const canExport = hasPermission(useApp((s) => s.account), "RELATORIOS.PRODUTOS.EXPORTAR");
  const [mode, setMode] = useState<"vendas" | "inativos">("vendas");
  const [options, setOptions] = useState<ProductReportOptions | null>(null);

  // Vendas filters — por padrão ao abrir, traz apenas o resultado do dia (hoje).
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [groupBy, setGroupBy] = useState<ProductReportGroupBy>("produto");
  const [productId, setProductId] = useState<string>(ALL);
  const [clientId, setClientId] = useState<string>(ALL);
  const [deliveryUnitId, setDeliveryUnitId] = useState<string>(ALL);

  const [report, setReport] = useState<ProductSalesReport | null>(null);
  const [loading, setLoading] = useState(false);

  // Inativos filters
  const [days, setDays] = useState(30);
  const [inactiveProductId, setInactiveProductId] = useState<string>(ALL);
  const [inactive, setInactive] = useState<InactiveProductsReport | null>(null);
  const [inactiveLoading, setInactiveLoading] = useState(false);

  useEffect(() => {
    fetchProductReportOptions()
      .then(setOptions)
      .catch(err => toast.error(getApiErrorMessage(err, "Erro ao carregar filtros")));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchProductSalesReport({
        from: from || undefined,
        to: to || undefined,
        groupBy,
        productId: productId !== ALL ? productId : undefined,
        clientId: clientId !== ALL ? clientId : undefined,
        deliveryUnitId: deliveryUnitId !== ALL ? deliveryUnitId : undefined,
      });
      setReport(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar relatório"));
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy, productId, clientId, deliveryUnitId]);

  const loadInactive = useCallback(async () => {
    setInactiveLoading(true);
    try {
      const data = await fetchInactiveProducts({
        days,
        productId: inactiveProductId !== ALL ? inactiveProductId : undefined,
      });
      setInactive(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar produtos sem venda"));
    } finally {
      setInactiveLoading(false);
    }
  }, [days, inactiveProductId]);

  // Auto-load on mount / when switching to a mode that has no data yet.
  useEffect(() => {
    if (mode === "vendas" && !report) void loadReport();
    if (mode === "inativos" && !inactive) void loadInactive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const exportSalesCsv = useCallback(() => {
    if (!report) return;
    const rows: (string | number)[][] = [];
    rows.push(["Cód.", "Produto", "Unidade", "Pedidos", "Quantidade", "Valor"]);
    const push = (list: ProductSalesRow[]) => {
      for (const p of list) {
        rows.push([
          p.productCode ?? "",
          p.productName,
          p.unit,
          p.orderCount,
          p.totalQuantity,
          p.totalValue.toFixed(2),
        ]);
      }
    };
    if (report.groups.length > 0) {
      for (const g of report.groups) {
        rows.push([g.label, "", "", g.orders, "", g.totalValue.toFixed(2)]);
        push(g.products);
        rows.push([]);
      }
    } else {
      push(report.products);
    }
    downloadCsv(rows, `produtos_${from || "all"}_${to || "all"}.csv`);
  }, [report, from, to]);

  const exportInactiveCsv = useCallback(() => {
    if (!inactive) return;
    const rows: (string | number)[][] = [["Cód.", "Produto", "Categoria", "Última venda", "Dias sem venda"]];
    for (const p of inactive.products) {
      rows.push([
        p.productCode ?? "",
        p.productName,
        p.category ?? "",
        p.neverSold ? "Nunca vendido" : fmtBRDate(p.lastSaleDate),
        p.neverSold ? "" : (p.daysSinceLastSale ?? ""),
      ]);
    }
    downloadCsv(rows, `produtos_sem_venda_${inactive.days}d.csv`);
  }, [inactive]);

  const statCards = useMemo(() => {
    if (!report) return [];
    const cards = [
      { label: "Pedidos", value: String(report.totalOrders), sub: "não cancelados" },
      { label: "Valor total", value: fmt(report.totalValue), sub: "no período" },
    ];
    for (const u of report.totalsByUnit) {
      cards.push({ label: `Qtd. em ${u.unit}`, value: fmtNumber(u.totalQuantity), sub: "vendida" });
    }
    return cards;
  }, [report]);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Relatório de Produtos</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Produtos vendidos por dia, cliente, unidade de entrega e período; itens mais vendidos e
              itens sem venda.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={mode === "vendas" ? "secondary" : "ghost"}
              size="sm"
              className="gap-1.5 text-primary-foreground data-[variant=ghost]:hover:bg-white/10"
              onClick={() => setMode("vendas")}
              aria-pressed={mode === "vendas"}
            >
              <TrendingUp className="h-4 w-4" /> Vendidos
            </Button>
            <Button
              variant={mode === "inativos" ? "secondary" : "ghost"}
              size="sm"
              className="gap-1.5 text-primary-foreground data-[variant=ghost]:hover:bg-white/10"
              onClick={() => setMode("inativos")}
              aria-pressed={mode === "inativos"}
            >
              <PackageX className="h-4 w-4" /> Sem venda +{days}d
            </Button>
          </div>
        </div>

        {/* Filters */}
        {mode === "vendas" ? (
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">De</span>
              <DatePicker
                value={from}
                toDate={to || undefined}
                onChange={next => { setFrom(next); if (next && to && next > to) setTo(next); }}
                className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Até</span>
              <DatePicker
                value={to}
                fromDate={from || undefined}
                onChange={next => { setTo(next); if (next && from && next < from) setFrom(next); }}
                className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Agrupar por</span>
              <Select value={groupBy} onValueChange={v => setGroupBy(v as ProductReportGroupBy)}>
                <SelectTrigger className="h-8 w-52 bg-white/10 border-white/20 text-primary-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(GROUP_BY_LABELS) as ProductReportGroupBy[]).map(k => (
                    <SelectItem key={k} value={k}>{GROUP_BY_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Produto</span>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="h-8 w-56 bg-white/10 border-white/20 text-primary-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os produtos</SelectItem>
                  {(options?.products ?? []).map(p => (
                    <SelectItem key={p.id} value={p.id}>{productLabel(p.code, p.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Cliente</span>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="h-8 w-48 bg-white/10 border-white/20 text-primary-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os clientes</SelectItem>
                  {(options?.clients ?? []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Unidade de entrega</span>
              <Select value={deliveryUnitId} onValueChange={setDeliveryUnitId}>
                <SelectTrigger className="h-8 w-48 bg-white/10 border-white/20 text-primary-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todas</SelectItem>
                  {(options?.deliveryUnits ?? []).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="secondary" className="h-8 gap-1.5" onClick={() => void loadReport()} disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />}
              Atualizar
            </Button>
            {canExport && report && (report.products.length > 0 || report.groups.length > 0) && (
              <Button variant="secondary" className="h-8 gap-1.5" onClick={exportSalesCsv}>
                <Download className="h-3.5 w-3.5" /> CSV
              </Button>
            )}
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Dias sem venda (mínimo)</span>
              <Input
                type="number"
                min={1}
                value={days}
                onChange={e => setDays(Math.max(1, Number(e.target.value) || 1))}
                className="h-8 w-32 bg-white/10 border-white/20 text-primary-foreground text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Produto</span>
              <Select value={inactiveProductId} onValueChange={setInactiveProductId}>
                <SelectTrigger className="h-8 w-56 bg-white/10 border-white/20 text-primary-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os produtos</SelectItem>
                  {(options?.products ?? []).map(p => (
                    <SelectItem key={p.id} value={p.id}>{productLabel(p.code, p.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="secondary" className="h-8 gap-1.5" onClick={() => void loadInactive()} disabled={inactiveLoading}>
              {inactiveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />}
              Atualizar
            </Button>
            {canExport && inactive && inactive.products.length > 0 && (
              <Button variant="secondary" className="h-8 gap-1.5" onClick={exportInactiveCsv}>
                <Download className="h-3.5 w-3.5" /> CSV
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Vendas */}
      {mode === "vendas" && (
        loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : report && report.products.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
            <Package className="h-10 w-10 opacity-30" />
            <p className="text-sm">Nenhum produto vendido no período</p>
          </div>
        ) : report ? (
          <>
            {statCards.length > 0 && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {statCards.map(card => (
                  <div key={card.label} className="rounded-xl border bg-card p-4 shadow-sm">
                    <div className="text-xs text-muted-foreground">{card.label}</div>
                    <div className="mt-1 text-xl font-bold tabular-nums">{card.value}</div>
                    <div className="text-xs text-muted-foreground">{card.sub}</div>
                  </div>
                ))}
              </div>
            )}

            {report.groups.length > 0 ? (
              <div className="flex flex-col gap-4">
                {report.groups.map(g => (
                  <div key={g.key ?? g.label} className="rounded-xl border bg-card shadow-sm">
                    <div className="flex items-center justify-between border-b px-4 py-3">
                      <span className="text-sm font-semibold">{g.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {g.orders} pedido(s) · {fmt(g.totalValue)}
                      </span>
                    </div>
                    <ProductsTable rows={g.products} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border bg-card shadow-sm">
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    Itens mais vendidos {from && to ? `· ${from} → ${to}` : ""}
                  </span>
                  <span className="text-xs text-muted-foreground">{report.products.length} itens</span>
                </div>
                <ProductsTable rows={report.products} />
              </div>
            )}
          </>
        ) : null
      )}

      {/* Sem venda */}
      {mode === "inativos" && (
        inactiveLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : inactive && inactive.products.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
            <PackageX className="h-10 w-10 opacity-30" />
            <p className="text-sm">Nenhum produto ativo sem venda há mais de {inactive.days} dias</p>
          </div>
        ) : inactive ? (
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <span className="text-sm font-medium text-muted-foreground">
                Produtos ativos sem venda há mais de {inactive.days} dias · referência {fmtBRDate(inactive.asOf)}
              </span>
              <span className="text-xs text-muted-foreground">{inactive.products.length} produtos</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cód.</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Última venda</TableHead>
                  <TableHead className="text-right">Dias sem venda</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inactive.products.map(p => (
                  <Fragment key={p.productId}>
                    <TableRow>
                      <TableCell className="tabular-nums text-muted-foreground">{p.productCode ?? "—"}</TableCell>
                      <TableCell className="font-medium">{p.productName}</TableCell>
                      <TableCell className="text-muted-foreground">{p.category ?? "—"}</TableCell>
                      <TableCell className="tabular-nums text-muted-foreground">
                        {p.neverSold ? (
                          <span className="text-amber-700">Nunca vendido</span>
                        ) : fmtBRDate(p.lastSaleDate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {p.neverSold ? "—" : p.daysSinceLastSale}
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null
      )}
    </div>
  );
}
